import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, ArrowRight, Plus, Edit3, Trash2, Loader2, Check, MapPin, RefreshCcw, ArrowLeftRight, Heart, CreditCard, IdCard, XCircle, Plane, Award, Leaf, Users, Settings, Eye, FileCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import EmirateDocsSection from '../components/services/EmirateDocsSection';
import ServiceFormModal from '../components/services/ServiceFormModal';
import EmiratePricingPanel from '../components/services/EmiratePricingPanel';
import { pricingSummary } from '../components/services/serviceEmirates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service type dropdown options — matches the filter categories on the
// customer platform ('خدمات التأشيرة', 'الهوية الإماراتية', 'التجديدات', 'الطبية',
// 'تغيير الحالة').

// أيقونات الخدمات
const SERVICE_ICONS = {
  RefreshCcw, ArrowLeftRight, Heart, CreditCard, IdCard, XCircle, Plane, Award, Leaf, Users, FileText, FileCheck
};

export default function VisaIdServices() {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const { language } = useLanguage();
  const { token } = useAuth();
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // عرض تفاصيل الخدمة
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState('details'); // details, documents
  const [masterDocs, setMasterDocs] = useState([]);
  const [serviceDocs, setServiceDocs] = useState([]);
  const [savingDocs, setSavingDocs] = useState(false);
  
  const [savingEmirates, setSavingEmirates] = useState(false);


  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // جلب الخدمات
  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/visa-services`, {
        headers: getHeaders()
      });
      if (response.data.success) {
        setServices(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error(language === 'ar' ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  // جلب الأوراق المطلوبة للخدمة
  const fetchServiceDocuments = async (serviceId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/master-documents/service/visa_services/${serviceId}`,
        { headers: getHeaders() }
      );
      if (response.data.success) {
        setMasterDocs(response.data.data);
        setServiceDocs(response.data.data.filter(d => d.is_enabled).map(d => d.id));
      }
    } catch (error) {
      console.error('Error fetching service documents:', error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // عند اختيار خدمة من URL
  useEffect(() => {
    if (serviceId && services.length > 0) {
      const service = services.find(s => s.id === serviceId);
      if (service) {
        setSelectedService(service);
        fetchServiceDocuments(service.id);
      }
    }
  }, [serviceId, services]);

  const openAddModal = () => { setEditingService(null); setShowModal(true); };
  const openEditModal = (service) => { setEditingService(service); setShowModal(true); };

  // Simplified form (Jun 2026): main/sub name + descriptions + active. Pricing is per emirate.
  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API_URL}/api/visa-services/${editingService.id}`, payload, { headers: getHeaders() });
        toast.success(language === 'ar' ? 'تم تحديث الخدمة بنجاح' : 'Service updated successfully');
      } else {
        await axios.post(`${API_URL}/api/visa-services`, payload, { headers: getHeaders() });
        toast.success(language === 'ar' ? 'تم إضافة الخدمة بنجاح' : 'Service added successfully');
      }
      setShowModal(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'خطأ في حفظ البيانات' : 'Error saving data'));
    } finally {
      setSaving(false);
    }
  };

  const saveEmirates = async (emirate_pricing) => {
    setSavingEmirates(true);
    try {
      await axios.put(`${API_URL}/api/visa-services/${selectedService.id}`, { emirate_pricing }, { headers: getHeaders() });
      const r = await axios.get(`${API_URL}/api/visa-services/${selectedService.id}`, { headers: getHeaders() });
      if (r.data?.data) setSelectedService(r.data.data);
      toast.success(language === 'ar' ? 'تم حفظ أسعار الإمارات' : 'Emirate prices saved');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'خطأ في حفظ الأسعار' : 'Error saving prices'));
    } finally {
      setSavingEmirates(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) {
      return;
    }

    setDeleting(serviceId);
    try {
      await axios.delete(`${API_URL}/api/visa-services/${serviceId}`, {
        headers: getHeaders()
      });
      toast.success(language === 'ar' ? 'تم حذف الخدمة بنجاح' : 'Service deleted successfully');
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error(language === 'ar' ? 'خطأ في حذف الخدمة' : 'Error deleting service');
    } finally {
      setDeleting(null);
    }
  };

  // Quick toggle active/inactive from the service card (without opening edit modal)
  const toggleServiceActive = async (service) => {
    const next = !service.is_active;
    // Optimistic UI
    setServices((prev) => prev.map((s) => s.id === service.id ? { ...s, is_active: next } : s));
    try {
      await axios.put(`${API_URL}/api/visa-services/${service.id}`, {
        is_active: next,
      }, { headers: getHeaders() });
      toast.success(
        next
          ? (language === 'ar' ? 'تم تفعيل الخدمة' : 'Service activated')
          : (language === 'ar' ? 'تم تعطيل الخدمة' : 'Service deactivated')
      );
    } catch (error) {
      // Revert
      setServices((prev) => prev.map((s) => s.id === service.id ? { ...s, is_active: !next } : s));
      toast.error(language === 'ar' ? 'فشل التحديث' : 'Update failed');
    }
  };

  // إضافة الخدمات الافتراضية — Legacy: backend auto-seeds silently on first GET.
  // Kept as no-op so any leftover handler still works.
  const seedDefaults = async () => {};

  // تحديث الأوراق المطلوبة للخدمة
  const toggleDocument = (docId) => {
    setServiceDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // حفظ الأوراق المطلوبة
  const saveServiceDocuments = async () => {
    if (!selectedService) return;
    
    setSavingDocs(true);
    try {
      await axios.post(
        `${API_URL}/api/master-documents/service/visa_services/${selectedService.id}`,
        { enabled_documents: serviceDocs },
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم حفظ الأوراق المطلوبة' : 'Required documents saved');
    } catch (error) {
      console.error('Error saving service documents:', error);
      toast.error(language === 'ar' ? 'خطأ في حفظ الأوراق' : 'Error saving documents');
    } finally {
      setSavingDocs(false);
    }
  };

  // عرض تفاصيل الخدمة
  const viewService = (service) => {
    setSelectedService(service);
    setActiveTab('details');
    fetchServiceDocuments(service.id);
    navigate(`/control/cloudgm/visa-id/${service.id}`);
  };

  const breadcrumbItems = selectedService ? [
    { pathAr: 'CLOUDGM FZ LLC', pathEn: 'CLOUDGM FZ LLC', href: '/dashboard?view=cloudgm' },
    { pathAr: 'منصة العملاء', pathEn: 'Customers Platform', href: '/dashboard?view=cloudgm&platform=customers' },
    { pathAr: 'التحكم والمعلومات', pathEn: 'Control & Info', href: '/control/cloudgm' },
    { pathAr: 'الطباعة وتخليص المستندات', pathEn: 'Printing & Document Clearance', href: '/control/cloudgm?view=printing-documents' },
    { pathAr: 'التأشيرات والهوية', pathEn: 'Visa & ID', href: '/control/cloudgm/visa-id' },
    { pathAr: selectedService.name_ar, pathEn: selectedService.name_en },
  ] : [
    { pathAr: 'CLOUDGM FZ LLC', pathEn: 'CLOUDGM FZ LLC', href: '/dashboard?view=cloudgm' },
    { pathAr: 'منصة العملاء', pathEn: 'Customers Platform', href: '/dashboard?view=cloudgm&platform=customers' },
    { pathAr: 'التحكم والمعلومات', pathEn: 'Control & Info', href: '/control/cloudgm' },
    { pathAr: 'الطباعة وتخليص المستندات', pathEn: 'Printing & Document Clearance', href: '/control/cloudgm?view=printing-documents' },
    { pathAr: 'التأشيرات والهوية', pathEn: 'Visa & ID' },
  ];

  // عرض تفاصيل خدمة محددة
  if (selectedService) {
    const IconComponent = SERVICE_ICONS[selectedService.icon] || FileText;

    return (
      <DashboardLayout
        title={language === 'ar' ? selectedService.name_ar : selectedService.name_en}
        titleEn={selectedService.name_en}
        subtitle={language === 'ar' ? 'إدارة بيانات الخدمة والأوراق المطلوبة' : 'Manage service details and required documents'}
        subtitleEn="Manage service details and required documents"
        backPath="/control/cloudgm/visa-id"
        onBack={() => {
          setSelectedService(null);
          navigate('/control/cloudgm/visa-id');
        }}
        breadcrumb={[
          { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
          { labelAr: 'منصة العملاء', labelEn: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
          { labelAr: 'التحكم والمعلومات (منصة العملاء)', labelEn: 'Control & Info (Customers)', path: '/control/cloudgm' },
          { labelAr: 'الطباعة وتخليص المستندات', labelEn: 'Printing & Document Clearance', path: '/control/cloudgm?view=printing-documents' },
          { labelAr: 'التأشيرات والهوية', labelEn: 'Visa & ID', path: '/control/cloudgm/visa-id' },
          { labelAr: selectedService.name_ar, labelEn: selectedService.name_en },
        ]}
      >
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="visa-service-detail">
          {/* Service identity strip */}
          <div className="flex items-center gap-4 bg-[#1a1a2e] border border-[#2a2a4e] rounded-2xl p-5">
            <div className="w-14 h-14 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <IconComponent className="h-7 w-7 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-white truncate">
                {language === 'ar' ? selectedService.name_ar : selectedService.name_en}
              </h1>
              <div className="flex items-center gap-4 text-sm text-[#A1A1AA] mt-1">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {pricingSummary(selectedService, language === 'ar').count}/7 {language === 'ar' ? 'إمارات' : 'emirates'} · {pricingSummary(selectedService, language === 'ar').text}
                </span>
                <span className={`flex items-center gap-1 ${selectedService.is_active ? 'text-emerald-300' : 'text-red-300'}`}>
                  <Check className="h-4 w-4" />
                  {selectedService.is_active ? (language === 'ar' ? 'مفعّل' : 'Active') : (language === 'ar' ? 'معطّل' : 'Inactive')}
                </span>
              </div>
            </div>
            <Button
              onClick={() => openEditModal(selectedService)}
              variant="outline"
              className="border-[#2a2a4e] text-white shrink-0"
              data-testid="edit-service-btn"
            >
              <Edit3 className="h-4 w-4 me-2" />
              {language === 'ar' ? 'تعديل' : 'Edit'}
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'details'
                  ? 'bg-[#c9a227] text-black'
                  : 'bg-[#1a1a2e] text-white hover:bg-[#2a2a4e]'
              }`}
              data-testid="tab-details"
            >
              <Eye className="h-4 w-4 inline me-2" />
              {language === 'ar' ? 'تفاصيل الخدمة' : 'Service Details'}
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'documents'
                  ? 'bg-[#c9a227] text-black'
                  : 'bg-[#1a1a2e] text-white hover:bg-[#2a2a4e]'
              }`}
              data-testid="tab-documents"
            >
              <FileCheck className="h-4 w-4 inline me-2" />
              {language === 'ar' ? 'الأوراق المطلوبة' : 'Required Documents'}
            </button>
          </div>

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6 space-y-4">
              {/* iter-274 — display Main + Sub service names when linked. */}
              {(selectedService.main_name_ar || selectedService.main_service_id) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.03]">
                  <div>
                    <p className="text-cyan-300 text-xs mb-1">{language === 'ar' ? 'اسم الخدمة الرئيسية' : 'Main Service Name'}</p>
                    <p className="text-white font-medium">
                      {language === 'ar'
                        ? (selectedService.main_name_ar || '-')
                        : (selectedService.main_name_en || selectedService.main_name_ar || '-')}
                    </p>
                  </div>
                  <div>
                    <p className="text-cyan-300 text-xs mb-1">{language === 'ar' ? 'اسم الخدمة الفرعية' : 'Sub Service Name'}</p>
                    <p className="text-white font-medium">
                      {language === 'ar'
                        ? (selectedService.name_ar || '-')
                        : (selectedService.name_en || selectedService.name_ar || '-')}
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <p className="text-[#808080] text-sm mb-1">{language === 'ar' ? 'الاسم بالعربية' : 'Arabic Name'}</p>
                  <p className="text-white font-medium">{selectedService.name_ar}</p>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <p className="text-[#808080] text-sm mb-1">{language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'}</p>
                  <p className="text-white font-medium">{selectedService.name_en}</p>
                </div>
              </div>

              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <p className="text-[#808080] text-sm mb-1">{language === 'ar' ? 'الوصف بالعربية' : 'Arabic Description'}</p>
                <p className="text-white">{selectedService.description_ar || '-'}</p>
              </div>

              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <p className="text-[#808080] text-sm mb-1">{language === 'ar' ? 'الوصف بالإنجليزية' : 'English Description'}</p>
                <p className="text-white">{selectedService.description_en || '-'}</p>
              </div>

              <EmiratePricingPanel
                service={selectedService}
                onSave={saveEmirates}
                saving={savingEmirates}
                language={language}
                accentTw={{ icon: 'text-emerald-400', border: 'border-emerald-500/40', switchOn: 'data-[state=checked]:bg-emerald-500' }}
                testIdPrefix="visa"
              />
            </div>
          )}

          {/* Documents Tab — per-emirate required documents */}
          {activeTab === 'documents' && (
            <EmirateDocsSection
              service={selectedService}
              language={language}
              testIdPrefix="visa"
            />
          )}
        </section>
      </DashboardLayout>
    );
  }

  // عرض قائمة الخدمات
  return (
    <DashboardLayout
      title="خدمات التأشيرات والهوية الإماراتية"
      titleEn="Visa & Emirates ID Services"
      subtitle="إدارة خدمات التأشيرات والهوية الإماراتية"
      subtitleEn="Manage visa and Emirates ID services"
      backPath="/control/cloudgm?view=printing-documents"
      breadcrumb={[
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
        { labelAr: 'منصة العملاء', labelEn: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
        { labelAr: 'التحكم والمعلومات (منصة العملاء)', labelEn: 'Control & Info (Customers)', path: '/control/cloudgm' },
        { labelAr: 'الطباعة وتخليص المستندات', labelEn: 'Printing & Document Clearance', path: '/control/cloudgm?view=printing-documents' },
        { labelAr: 'التأشيرات والهوية', labelEn: 'Visa & ID' }
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <FileText className="h-6 w-6 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {language === 'ar' ? 'الخدمات المتاحة' : 'Available Services'}
              </h2>
              <p className="text-sm text-[#71717A]">
                {services.length} {language === 'ar' ? 'خدمة' : 'services'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={openAddModal}
              className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
              data-testid="add-visa-service-btn"
            >
              <Plus className="h-4 w-4 me-2" />
              {language === 'ar' ? 'إضافة خدمة جديدة' : 'Add New Service'}
            </Button>
          </div>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-20 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
            <FileText className="h-16 w-16 mx-auto mb-4 text-emerald-500 opacity-30" />
            <p className="text-lg text-[#808080]">
              {language === 'ar' ? 'لا توجد خدمات بعد' : 'No services yet'}
            </p>
            <p className="text-sm text-[#606060] mt-2">
              {language === 'ar' 
                ? 'اضغط على "إضافة الافتراضيات" لإضافة خدمات التأشيرات الشائعة' 
                : 'Click "Add Defaults" to add common visa services'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = SERVICE_ICONS[service.icon] || FileText;
              return (
                <div
                  key={service.id}
                  className={`bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden hover:border-emerald-500/50 transition-all group flex flex-col ${!service.is_active ? 'opacity-70' : ''}`}
                  data-testid={`visa-service-card-${service.id}`}
                >
                  {/* Top toolbar: active toggle (left) + edit/delete (right) */}
                  <div className="flex items-center justify-between px-6 pt-4 pb-2 border-b border-white/5">
                    <div className="flex items-center gap-2" data-testid={`visa-service-active-${service.id}`}>
                      <Switch
                        checked={!!service.is_active}
                        onCheckedChange={() => toggleServiceActive(service)}
                        className="data-[state=checked]:bg-emerald-500"
                      />
                      <span className={`text-[11px] font-medium ${service.is_active ? 'text-emerald-300' : 'text-[#71717A]'}`}>
                        {service.is_active
                          ? (language === 'ar' ? 'مفعّلة' : 'Active')
                          : (language === 'ar' ? 'موقوفة' : 'Paused')}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditModal(service)}
                        className="p-2 rounded-lg bg-[#0a0a0a] hover:bg-[#2a2a4e] transition-colors"
                        data-testid={`visa-service-edit-${service.id}`}
                      >
                        <Edit3 className="h-4 w-4 text-blue-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(service.id)}
                        disabled={deleting === service.id}
                        className="p-2 rounded-lg bg-[#0a0a0a] hover:bg-red-500/20 transition-colors"
                        data-testid={`visa-service-delete-${service.id}`}
                      >
                        {deleting === service.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-red-400" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-red-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Body — grows to fill available space so the CTA sits at the bottom */}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform mb-4">
                      <IconComponent className="h-7 w-7 text-emerald-500" />
                    </div>

                    <h3 className="text-lg font-bold text-white mb-2">
                      {language === 'ar' ? service.name_ar : service.name_en}
                    </h3>
                    <p className="text-[#808080] text-sm mb-4 line-clamp-2">
                      {language === 'ar' ? service.description_ar : service.description_en}
                    </p>

                    <div className="flex items-center justify-between text-sm mt-auto">
                      <span className="text-[#c9a227] font-bold" data-testid={`visa-card-total-${service.id}`}>
                        {pricingSummary(service, language === 'ar').text}
                      </span>
                      <span className="text-[#808080] flex items-center gap-1" data-testid={`visa-card-emirates-${service.id}`}>
                        <MapPin className="h-4 w-4" />
                        {pricingSummary(service, language === 'ar').count}/7 {language === 'ar' ? 'إمارات' : 'emirates'}
                      </span>
                    </div>
                  </div>

                  {/* CTA anchored to bottom via flex-col */}
                  <button
                    onClick={() => viewService(service)}
                    data-testid={`visa-service-manage-${service.id}`}
                    className="w-full p-3 bg-emerald-500/10 text-emerald-400 text-sm font-medium hover:bg-emerald-500/20 transition-colors flex items-center justify-center gap-2 border-t border-white/5"
                  >
                    <Settings className="h-4 w-4" />
                    {language === 'ar' ? 'إدارة الخدمة' : 'Manage Service'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <ServiceFormModal
          open={showModal}
          onOpenChange={setShowModal}
          service={editingService}
          onSubmit={handleSave}
          saving={saving}
          language={language}
          PageIcon={IdCard}
          accentIcon="text-emerald-400"
          switchOn="data-[state=checked]:bg-emerald-500"
          testIdPrefix="visa"
        />
      </section>
    </DashboardLayout>
  );
}
